import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Badge,
  Row,
  Col,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import SettingsPageHeader from '../components/SettingsPageHeader';
import { PlusIcon, EditIcon } from '../components/icons/SettingsIcons';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { nurseryAPI, clearCache } from '../utils/api';
import TimezoneSelect from '../components/TimezoneSelect';
import type { Nursery, CreateNurseryRequest, UpdateNurseryRequest } from '../types/auth';
import type { InheritableSettings } from '../types/nurseryGroup';

const NurseryManagement: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'nurseryManagement' });
  const { t: tc } = useTranslation('common');
  const { currentNursery, userRolePermissionsLoading } = useAuth();
  const {
    canCreateNurseries,
    canAccessNurseryManagement
  } = useRolePermissions();

  const [nurseries, setNurseries] = useState<Nursery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedNursery, setSelectedNursery] = useState<Nursery | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // Form states
  const [formData, setFormData] = useState<CreateNurseryRequest>({
    nursery_id: '',
    name: '',
    status: 1,
    email: '',
    phone: '',
    address: '',
    description: '',
    timezone: 'UTC',
    nursery_location_id: null
  });
  const [overrides, setOverrides] = useState<Partial<InheritableSettings>>({});

  // Nurseries list is global (not filtered by sidebar). Locations dropdown for "Assign to location"
  // is scoped to the sidebar-selected nursery (currentNursery) — chosen approach per task spec.
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [compRes] = await Promise.all([
        nurseryAPI.getNurseries(),
      ]);
      setNurseries(compRes.data);
    } catch {
      setError(t('fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearCache('nurseries');
    fetchData();
  }, [fetchData]);


  const resetForm = () => {
    setFormData({
      nursery_id: '',
      name: '',
      status: 1,
      email: '',
      phone: '',
      address: '',
      description: '',
      timezone: 'UTC',
      nursery_location_id: null
    });
    setOverrides({});
    setModalError(null);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      setModalError(null);

      const payload: Record<string, any> = {
        nursery_id: formData.nursery_id,
        name: formData.name,
        status: formData.status,
        email: formData.email || '',
        phone: formData.phone || '',
        nursery_location_id: formData.nursery_location_id ?? null,
        timezone: formData.timezone
      };

      if (formData.description) payload.description = formData.description;
      if (formData.address) payload.address = formData.address;

      if (formData.nursery_location_id) {
        const keys: (keyof InheritableSettings)[] = [
          'timezone', 'address', 'repo_th', 'valid_th_from', 'valid_th_to', 'similarity_addition'
        ];
        keys.forEach(key => {
          let value = overrides[key];
          if (value !== undefined && value !== null) {
            payload[key] = value;
          }
        });
      } else {
        if (overrides.repo_th != null) payload.repo_th = overrides.repo_th;
        if (overrides.valid_th_from != null) payload.valid_th_from = overrides.valid_th_from;
        if (overrides.valid_th_to != null) payload.valid_th_to = overrides.valid_th_to;
        if (overrides.similarity_addition != null) payload.similarity_addition = overrides.similarity_addition;
      }

      console.log('📤 NurseryManagement: Create payload:', JSON.stringify(payload, null, 2));
      await nurseryAPI.createNursery(payload as CreateNurseryRequest);
      setSuccess(t('createSuccess'));
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (err: any) {
      setModalError(err.response?.data?.message || t('saveFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const sanitizePlaceholder = (value: any) => {
    if (typeof value === 'string' && (value === '[value-12]' || value.includes('[value-'))) {
      return '';
    }
    return value;
  };

  const handleEditClick = async (nursery: Nursery) => {
    setSelectedNursery(nursery);
    setFormData({
      nursery_id: nursery.nursery_id,
      name: nursery.name,
      status: nursery.status,
      email: nursery.email || '',
      phone: nursery.phone || '',
      address: sanitizePlaceholder(nursery.address),
      description: nursery.description || '',
      timezone: nursery.timezone || 'UTC',
      nursery_location_id: nursery.nursery_location_id
    });

    const initialOverrides: Partial<InheritableSettings> = {};
    const keys: (keyof InheritableSettings)[] = [
      'timezone', 'address', 'repo_th', 'valid_th_from', 'valid_th_to', 'similarity_addition'
    ];
    keys.forEach(key => {
      let value = (nursery as any)[key];
      
      // Sanitize placeholder strings
      if (typeof value === 'string' && (value === '[value-12]' || value.includes('[value-'))) {
        value = null;
      }

      if (value !== null && value !== undefined) {
        (initialOverrides as any)[key] = value;
      }
    });
    setOverrides(initialOverrides);
    setModalError(null);
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedNursery) return;
    try {
      setActionLoading(true);
      setModalError(null);
      
      const updateData: UpdateNurseryRequest = {
        name: formData.name,
        status: formData.status,
        email: formData.email ?? undefined,
        phone: formData.phone ?? undefined,
        address: formData.address ?? undefined,
        description: formData.description ?? undefined,
        timezone: formData.timezone,
        nursery_location_id: formData.nursery_location_id
      };

      const keys: (keyof InheritableSettings)[] = [
        'timezone', 'address', 'repo_th', 'valid_th_from', 'valid_th_to', 'similarity_addition'
      ];
      keys.forEach(key => {
        let value = overrides[key];
        
        // If assigned to a location, overrides take precedence (null means inherit)
        // If standalone, address and timezone from formData take precedence
        if ((key === 'address' || key === 'timezone') && !formData.nursery_location_id) {
          // Already set from formData in updateData initialization, do not overwrite
        } else {
          (updateData as any)[key] = value === undefined ? null : value;
        }
      });

      await nurseryAPI.updateNursery(selectedNursery.id, updateData);
      setSuccess(t('updateSuccess'));
      setShowEditModal(false);
      resetForm();
      fetchData();
    } catch (err: any) {
      setModalError(err.response?.data?.message || t('saveFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const renderNurseryFormFields = (isEdit: boolean = false) => (
    <Row className="g-3">
      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label className="fw-bold">{t('nurseryIdLabel')} <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            value={formData.nursery_id}
            onChange={(e) => setFormData({ ...formData, nursery_id: e.target.value })}
            required
            disabled={isEdit}
          />
        </Form.Group>
      </Col>
      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label className="fw-bold">{t('nurseryNameLabel')} <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </Form.Group>
      </Col>
      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label className="fw-bold">{t('emailAddress')}</Form.Label>
          <Form.Control
            type="email"
            value={formData.email || ''}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
        </Form.Group>
      </Col>
      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label className="fw-bold">{t('phoneNumber')}</Form.Label>
          <Form.Control
            type="tel"
            value={formData.phone || ''}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
        </Form.Group>
      </Col>
      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label className="fw-bold">{t('status')}</Form.Label>
          <Form.Select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: Number(e.target.value) })}
          >
            <option value={1}>{tc('status.active')}</option>
            <option value={2}>{tc('status.inactive')}</option>
          </Form.Select>
        </Form.Group>
      </Col>

      
        
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label className="fw-bold">{t('timezone')} <span className="text-danger">*</span></Form.Label>
              <TimezoneSelect
                value={formData.timezone}
                onChange={(val) => setFormData({ ...formData, timezone: val || 'UTC' })}
                useDetectedAsDefault={!isEdit}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label className="fw-bold">{t('repositoryThreshold')}</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={overrides.repo_th ?? ''}
                onChange={(e) => setOverrides(prev => ({ ...prev, repo_th: e.target.value === '' ? null : parseFloat(e.target.value) }))}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label className="fw-bold">{t('validationThresholdFrom')}</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={overrides.valid_th_from ?? ''}
                onChange={(e) => setOverrides(prev => ({ ...prev, valid_th_from: e.target.value === '' ? null : parseFloat(e.target.value) }))}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label className="fw-bold">{t('validationThresholdTo')}</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={overrides.valid_th_to ?? ''}
                onChange={(e) => setOverrides(prev => ({ ...prev, valid_th_to: e.target.value === '' ? null : parseFloat(e.target.value) }))}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label className="fw-bold">{t('similarityAddition')}</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={overrides.similarity_addition ?? ''}
                onChange={(e) => setOverrides(prev => ({ ...prev, similarity_addition: e.target.value === '' ? null : parseFloat(e.target.value) }))}
              />
            </Form.Group>
          </Col>
          <Col xs={12}>
            <Form.Group>
              <Form.Label className="fw-bold">{t('address')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.address || ''}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Form.Group>
          </Col>
     

      <Col xs={12}>
        <Form.Group>
          <Form.Label className="fw-bold">{t('description')}</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </Form.Group>
      </Col>
    </Row>
  );

  const settingsPath = currentNursery ? `/nursery/${currentNursery.nursery_id}/settings` : '/settings';

  if (userRolePermissionsLoading || loading) {
    return (
      <Layout title={t('title')}>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
        </Container>
      </Layout>
    );
  }

  if (!canAccessNurseryManagement) {
    return (
      <Layout title={t('title')}>
        <Container className="py-5">
          <Alert variant="danger">
            {t('noAccess')}
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="px-3 py-4">
        <SettingsPageHeader
          title={t('title')}
          settingsPath={settingsPath}
        />

        <Alert variant="info" className="shadow-sm border-0 bg-white mb-4">
          <div className="d-flex align-items-center">
            <div className="me-3 fs-4 text-info">
              <i className="bi bi-info-circle-fill"></i>
            </div>
            <div>
              <h6 className="fw-bold mb-1">{t('infoTitle')}</h6>
              <p className="mb-0 small text-muted" dangerouslySetInnerHTML={{ __html: t('infoBody') }} />
            </div>
          </div>
        </Alert>

        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Card className="shadow-sm border-0">
          <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
            <h5 className="mb-0 fw-bold">{t('nurseriesList')}</h5>
            {canCreateNurseries && (
              <Button variant="success" size="sm" onClick={() => { resetForm(); setShowCreateModal(true); }}>
                <PlusIcon /> {t('addNursery')}
              </Button>
            )}
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0 align-middle">
              <thead className="bg-light">
                <tr>
                  <th className="px-4">{t('name')}</th>
                  <th>{t('id')}</th>
                  <th>{t('email')}</th>
                  <th>{t('status')}</th>
                  <th className="text-end px-4">{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {nurseries.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-5 text-muted">
                      {t('noNurseries')}
                    </td>
                  </tr>
                ) : (
                  nurseries.filter(item => item.nursery_location_id == null ).map((nursery) => (
                    <tr key={nursery.id}>
                      <td className="px-4 fw-medium">{nursery.name}</td>
                      <td><code className="small">{nursery.nursery_id}</code></td>
                      <td className="text-muted small">{nursery.email}</td>
                      <td>
                        <Badge bg={nursery.status === 1 ? 'success' : 'danger'}>
                          {nursery.status === 1 ? tc('status.active') : tc('status.inactive')}
                        </Badge>
                      </td>
                      <td className="text-end px-4">
                        <Button variant="link" className="p-0 text-primary" onClick={() => handleEditClick(nursery)}>
                          <EditIcon />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* Create Modal */}
        <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
          <Form onSubmit={handleCreate}>
            <Modal.Header closeButton>
              <Modal.Title>{t('addNewNursery')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {modalError && <Alert variant="danger">{modalError}</Alert>}
              {renderNurseryFormFields(false)}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowCreateModal(false)}>{t('cancel', { ns: 'common', keyPrefix: 'buttons' })}</Button>
              <Button variant="success" type="submit" disabled={actionLoading}>
                {actionLoading ? <Spinner animation="border" size="sm" /> : t('createNursery')}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Edit Modal */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
          <Form onSubmit={handleUpdate}>
            <Modal.Header closeButton>
              <Modal.Title>{t('editNursery')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {modalError && <Alert variant="danger">{modalError}</Alert>}
              {renderNurseryFormFields(true)}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowEditModal(false)}>{t('cancel', { ns: 'common', keyPrefix: 'buttons' })}</Button>
              <Button variant="primary" type="submit" disabled={actionLoading}>
                {actionLoading ? <Spinner animation="border" size="sm" /> : t('saveChanges')}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </Layout>
  );
};

export default NurseryManagement;
